/**
 * `hierarchyId.ts` — Campfire `PartData.HierachyID` derivation for an
 * imported model tree. Pure + unit-testable (no Three.js side effects).
 *
 * ## The layout Unity's importer writes
 *
 * `BaseImporterModel.PostprocessImport` turns every imported file into
 *
 *     <file name>              "0,"      the entity root (`WrapAndShiftOrigin`'s wrapper)
 *       ├─ Origin              "0,0,"    empty pivot marker (`AddOriginGo`, sibling 0)
 *       └─ Root                "0,1,"    the file's content root, renamed
 *            └─ …model subtree…  "0,1,i,…,"
 *
 * and `ProjectModel_UploadAsset` writes one part row per transform in that
 * tree, so `Origin` and `Root` are real rows the hierarchy panel lists. The
 * web importer writes the same rows (`WebImporterModel.buildParsedModel`);
 * `gltf.scene` here IS that content root.
 *
 * On the READ side, `sceneEntityRenderer.placeEntityAsync` rebuilds the same
 * tree (`walkRoot` → inserted `Origin` at child 0 → `clone(gltf.scene)` at
 * child 1) and decodes an id with `getPartByHierarchyID`. Because the rows
 * include the `Origin` snapshot, it decodes them without a shift:
 *
 *     "0,"        -> walkRoot                       (the entity root)
 *     "0,0,"      -> walkRoot.children[0] = Origin
 *     "0,1,"      -> walkRoot.children[1] = "Root"  (= clone(gltf.scene))
 *     "0,1,i,"    -> Root.children[i]     = gltf.scene.children[i]
 *
 * ⚠ Rows written before 2026-10-08 have NO `Origin` row and put `gltf.scene`
 * at `"0,0,"` — the legacy layout, which both clients still read by adding 1 to
 * the first index (Unity's `ShiftHierarchyIDs`, the renderer's `shift`). The
 * `Origin` row is what selects between the two, so it must be written whenever
 * the content root is authored at `"0,1,"`: one without the other binds every
 * part one sibling off.
 *
 * ⚠ `gltf.scene` here means the UNITY content root, which is what the parse
 * step leaves in that slot (`runtime/assets/gltfUnityLayout.ts`): the loader's
 * scene wrapper for a multi-root or animated file, and the single root node
 * itself otherwise — Unity's `TryToRemoveEmptyLayers` strips the wrapper in
 * that case, so ids authored against it would read one level too deep on
 * desktop. The importer never sees the raw loader tree.
 *
 * This mirrors Unity's `CreateHierarchyID` (`ProjectModel_Utils.cs:127`),
 * which walks up from a part to the tagged root prepending sibling indices.
 */

import type { Object3D } from "three";
import { K_CONTENT_ROOT_HIERARCHY_ID } from "@models/data/entityData";

/** HierarchyID of the entity's root snapshot (the synthetic wrapper). */
export const ROOT_HIERARCHY_ID = "0,";

/**
 * Depth-first flatten of a node's subtree — the node itself first, then each
 * child recursively in child order. Matches Unity's
 * `GetComponentsInChildren<Transform>(true)` ordering used by the upload
 * pipeline so part / snapshot indices line up 1:1.
 */
export function flattenDepthFirst(root: Object3D): Object3D[] {
  const out: Object3D[] = [];
  const walk = (node: Object3D): void => {
    out.push(node);
    for (const child of node.children) walk(child);
  };
  walk(root);
  return out;
}

/**
 * Derive the `HierachyID` for `node` within the model tree rooted at
 * `sceneRoot` (= `gltf.scene`, the content root Unity renames `"Root"`).
 * `node` must be `sceneRoot` or a descendant.
 *
 *   sceneRoot                         -> "0,1,"
 *   sceneRoot.children[i]             -> "0,1,i,"
 *   sceneRoot.children[i].children[j] -> "0,1,i,j,"
 */
export function deriveHierarchyId(node: Object3D, sceneRoot: Object3D): string {
  const path: number[] = [];
  let cur: Object3D | null = node;
  while (cur && cur !== sceneRoot && cur.parent) {
    path.unshift(cur.parent.children.indexOf(cur));
    cur = cur.parent;
  }
  return path.length > 0
    ? `${K_CONTENT_ROOT_HIERARCHY_ID}${path.join(",")},`
    : K_CONTENT_ROOT_HIERARCHY_ID;
}
